import { X, Radio, MessageSquare, Send, Briefcase } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'

interface BroadcastSheetProps {
  open: boolean
  onClose: () => void
  onPostInsight: () => void
  onSendSMS: () => void
  onPostPortfolioUpdate?: () => void
}

export function BroadcastSheet({
  open,
  onClose,
  onPostInsight,
  onSendSMS,
  onPostPortfolioUpdate,
}: BroadcastSheetProps) {
  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh] w-full sm:max-w-md">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Radio className="w-5 h-5 text-black" />
            </div>
            <div>
              <SheetTitle className="text-xl font-bold text-white">
                Broadcast Panel
              </SheetTitle>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-6 space-y-6">
          {/* Description */}
          <div className="px-6">
            <p className="text-neutral-400 text-sm">
              Choose how you want to broadcast to your subscribers
            </p>
          </div>

          {/* Options */}
          <div className="px-6 space-y-4">
            {/* Post Trading Insight Option */}
            <button
              onClick={() => {
                onClose()
                onPostInsight()
              }}
              className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-500/10 to-emerald-500/10 border border-cyan-500/20 hover:border-cyan-400/40 p-5 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/30">
                  <MessageSquare className="w-7 h-7 text-black" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-white font-semibold text-lg mb-1.5">
                    Post Trading Insight
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">
                    Share detailed market analysis, trade signals, and insights
                    with charts and tags
                  </p>
                </div>
              </div>
            </button>

            {/* Send SMS Alert Option */}
            <button
              onClick={() => {
                onClose()
                onSendSMS()
              }}
              className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 hover:border-emerald-400/40 p-5 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
                  <Send className="w-7 h-7 text-black" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-white font-semibold text-lg mb-1.5">
                    Send SMS Alert
                  </h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">
                    Send urgent alerts and time-sensitive notifications directly
                    via SMS
                  </p>
                </div>
              </div>
            </button>
            {/* Post Portfolio Update Option */}
            {onPostPortfolioUpdate && (
              <button
                onClick={() => {
                  onClose()
                  onPostPortfolioUpdate()
                }}
                className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-violet-500/20 hover:border-violet-400/40 p-5 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                    <Briefcase className="w-7 h-7 text-black" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-white font-semibold text-lg mb-1.5">
                      Post Portfolio Update
                    </h3>
                    <p className="text-neutral-400 text-sm leading-relaxed">
                      Share portfolio screenshots, positions, and performance
                      commentary
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Tip Box */}
          <div className="px-6">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4">
              <p className="text-sm text-emerald-400 leading-relaxed">
                <span className="font-semibold">💡 Tip:</span> Use SMS alerts
                for urgent, time-sensitive updates. Post insights for detailed
                analysis and ongoing market commentary.
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
